import { useGroupChannelContext } from '@sendbird/uikit-react/GroupChannel/context';
import { type ReactNode, useState } from 'react';
import { resolveBrowserLocale, useTranslate } from 'ra-core';
import {
  type PinnedMessage,
  useFetchPinnedMessages,
} from '../../../../api/hooks/sendbird/useFetchPinnedMessages';
import { Spinner } from '../../../../components/admin/spinner';
import { Avatar, AvatarImage } from '../../../../components/ui/avatar';
import { Button } from '../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { cn } from '../../../../lib/utils';

type PinnedMessagesPopoverProps = {
  trigger: ReactNode;
  triggerLabel: string;
};

export const PinnedMessagesPopover = ({
  trigger,
  triggerLabel,
}: PinnedMessagesPopoverProps) => {
  const [selectedMessageId, setSelectedMessageId] = useState<number>();
  const [open, setOpen] = useState(false);
  const { data: pinnedMessages, isError, isPending } = useFetchPinnedMessages();
  const locale = resolveBrowserLocale();
  const translate = useTranslate();
  const { scrollToMessage } = useGroupChannelContext();

  const handleOnPinnedClick = (message: PinnedMessage) => {
    setSelectedMessageId(message.id);
    void scrollToMessage(message.date, message.id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={triggerLabel}
          />
        }
      >
        {trigger}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[min(70vh,32rem)] min-w-[300px] overflow-y-auto p-0"
      >
        {isPending ? (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="p-8 text-sm text-muted-foreground">
            {translate('chat.pinnedMessagesCouldNotBeFetched')}
          </p>
        ) : pinnedMessages.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground">
            {translate('chat.pinnedMessagesNotFound')}
          </p>
        ) : (
          pinnedMessages.map((message) => (
            <button
              type="button"
              key={message.id}
              onClick={() => handleOnPinnedClick(message)}
              className={cn(
                'flex w-full cursor-pointer gap-4 border-0 border-l-4 p-4 text-left transition-colors',
                message.id === selectedMessageId
                  ? 'border-l-[var(--chat-primary-400)] bg-[var(--chat-primary-100)] hover:bg-[var(--chat-primary-200)]'
                  : 'border-l-muted bg-background hover:bg-muted'
              )}
            >
              <Avatar size="lg" className="size-14">
                <AvatarImage src={message.avatarUrl} alt="" />
              </Avatar>

              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{message.displayName}</p>
                <p className="break-words text-sm">{message.content}</p>
              </div>
              <time className="shrink-0 text-sm text-muted-foreground">
                {new Date(message.date).toLocaleDateString(locale, {
                  day: 'numeric',
                  month: 'short',
                })}
              </time>
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};
